/**
 * 特殊マス（東日本・畿内側）
 *
 * 京(0) / 草津宿(2) / 桶狭間(5) / 箱根関(9) / 評定(12) / 白河関(15)
 *
 * すべて SquareBase。国マスではないので kokudaka・defense・terrain は持たない。
 * effects はルール実装側で扱うため、ここでは史実メモに集中している。
 *
 * 執筆基準は provinces/_reference.ts と docs/05-学習設計.md §4 に従う。
 */

import type { SquareBase } from '../types';

/** 0番。盤の起点。天下点と上洛の中心。 */
export const KYOTO: SquareBase = {
  id: 0,
  kind: 'capital',
  name: '京（山城）',
  reading: 'きょう（やましろ）',
  region: 'kinai',
  note: {
    summary:
      '天皇と朝廷が置かれた都。応仁の乱で市街の多くが焼け、上京と下京に分かれた町を町衆が自ら運営し、祇園祭を再興した。',
    detail: {
      1: 'いまの京都です。天皇がいる、日本の中心の都でした。しかし1467年からはじまった応仁の乱で11年も戦いがつづき、まちの多くがやけてしまいました。それでも京にすむ人びとは自分たちのまちを立てなおし、祇園祭をふたたびおこないました。',
      2: '現在の京都市街を中心とする山城国。天皇と朝廷が所在し、室町幕府も置かれた政治と文化の中心地。1467年から1477年の応仁の乱では京都そのものが主戦場となり、市街の多くが焼失した。乱の後、京は上京と下京という二つの町のまとまりに分かれ、それぞれ堀や塀で囲まれた。町の運営は町衆と呼ばれる商工業者が担い、1500年には途絶えていた祇園祭の山鉾巡行が再興されている。将軍の力は1493年の明応の政変以降さらに弱まり、細川氏や三好氏が実権を握った。1568年に織田信長が足利義昭を奉じて入京し、1573年に義昭が京を追われて室町幕府は事実上滅んだ。',
      3: '現在の京都市街を中心とする山城国。平安京以来の都であり、天皇と朝廷が所在し、室町幕府もここに置かれた。応仁元年(1467)に始まる応仁・文明の乱は、細川勝元の東軍と山名宗全の西軍が京都市中で戦い、文明9年(1477)まで続いた。戦火で上京・下京の多くが焼け、公家や僧侶が地方へ下ったことは、結果として京都の文化が各地へ広がる契機ともなった。乱後の京都は上京と下京の二つの町区に分かれ、それぞれ堀と塀（構）で囲まれた。町の運営は町衆と呼ばれる商工業者が担い、明応9年(1500)には祇園祭の山鉾巡行が再興されている。一方、南山城では文明17年(1485)に国人と農民が畠山氏の両軍を国外へ退去させ、8年間にわたり自治を行った（山城国一揆）。朝廷の財政は逼迫し、後柏原天皇は即位から即位の礼を挙げるまでに20年以上を要している。それでも官位を授ける権威は失われず、戦国大名にとって上洛と官位の獲得は重要な目標であり続けた。近年の研究では「京都が完全な焼け野原になった」という理解は見直され、被害の程度が地区によって差があったこと、乱の途中から復興が始まっていたことが指摘されている。',
    },
    year: [1467, 1600],
    keywords: ['京', '山城', '応仁の乱', '町衆', '祇園祭', '上洛'],
    caveat:
      '「応仁の乱で京都は焼け野原になった」とよく言われますが、被害の程度は地区によって差があり、乱の最中から復興が進んでいた場所もあったことが近年の研究で指摘されています。また祇園祭の再興を町衆の力だけで説明する見方も、疫病への不安といった当時の社会状況を含めて見直されています。',
    gameNote:
      '盤上では第一章の時代法として「京は誰も領有できない（荒廃）」という扱いにしていますが、これは応仁の乱の混乱を遊びに落とし込んだものです。実際には朝廷も室町幕府も京に存在し続けており、京が無主の地になったわけではありません。',
  },
};

/** 2番。東海道と中山道が出会う近江の要衝。カード購入と賭場。 */
export const KUSATSU_SHUKU: SquareBase = {
  id: 2,
  kind: 'post',
  name: '草津宿',
  reading: 'くさつじゅく',
  region: 'kinai',
  note: {
    summary:
      '東海道と中山道が出会う近江の交通の要衝。琵琶湖の水運と陸路が結びつき、荷を運ぶ馬借や問丸が行き交った。',
    detail: {
      1: 'いまの滋賀県草津市にあたる場所です。京へむかう二つの大きな道がここで一つになるので、たくさんの人と荷物がとおりました。荷物を馬ではこぶ馬借（ばしゃく）とよばれる人たちがはたらいていました。',
      2: '現在の滋賀県草津市にあたる、近江国栗太郡の交通の要衝。東海道と中山道がここで合流して京へ向かう。琵琶湖の水運と陸路が結びつく位置にあり、荷物を馬で運ぶ馬借、車で運ぶ車借、運送と倉庫を兼ねる問丸といった人びとが活動した。彼らは単なる運び手ではなく、1428年の正長の土一揆が近江坂本の馬借の蜂起から始まったように、集団で動く力を持っていた。宿場町として制度的に整えられるのは江戸時代に入ってからで、1601年の東海道の伝馬制、翌年の中山道の整備によって草津宿が置かれた。',
      3: '現在の滋賀県草津市。近江国栗太郡に属し、東海道と中山道が合流して京へ至る地点にあたる。琵琶湖南岸に位置し、大津・坂本などの湖上水運の湊と、東国へ向かう陸路とを結ぶ結節点であった。中世には旅人を泊める宿、荷を扱う問丸、馬で運搬する馬借、牛車を用いる車借などが集まり、物流の担い手として自立した集団を形成している。正長元年(1428)の正長の土一揆が近江坂本の馬借の蜂起を発端としたことは、彼らが情報と組織を備えた存在であったことを示す。一方、街道には荘園領主や寺社が関所を設けて関銭を徴収しており、流通の妨げにもなっていた。織田信長は永禄11年(1568)以降、支配下の関所を撤廃して流通を促している。「東海道五十三次の草津宿」として知られる本陣・脇本陣・問屋場を備えた宿場町の姿は、慶長6年(1601)以降の徳川政権下で整えられた近世のものである。街道を行き交ったのは武士や商人だけではなく、伊勢参りなどの旅に出る一般の人びと、荷を担いで売り歩く女性たちも含まれていた。',
    },
    year: [1467, 1601],
    keywords: ['草津', '近江', '東海道', '中山道', '馬借', '問丸'],
    caveat:
      '「東海道五十三次の草津宿」として知られる宿場町の姿は江戸時代のもので、宿駅制度が整うのは慶長6年(1601)以降です。戦国期の草津は制度化された宿場ではなく、交通の要衝に自然に形づくられた宿と流通の拠点でした。',
    gameNote:
      '盤上では戦国時代の盤面に「宿場」マスを置いていますが、これは街道の要衝に人・物・情報が集まる場をひとつのマスにまとめた表現です。江戸時代の宿駅制度をそのまま持ち込んだものではありません。',
  },
};

/** 5番。第三章の固定合戦イベント。 */
export const OKEHAZAMA: SquareBase = {
  id: 5,
  kind: 'battle',
  name: '桶狭間',
  reading: 'おけはざま',
  region: 'tokai',
  note: {
    summary:
      '1560年、尾張の桶狭間で織田信長の軍が今川義元の軍と戦い、義元が討たれた。兵の少ない側が大軍を破った例として語られてきた。',
    detail: {
      1: '1560年、いまの愛知県にある桶狭間で、織田信長の軍と今川義元の軍が戦いました。人数のすくない信長がわが勝ち、義元はうちとられました。このできごとのあと、信長の名は広くしられるようになりました。',
      2: '永禄3年(1560)、尾張国桶狭間（現在の愛知県豊明市から名古屋市緑区のあたり）で行われた戦い。駿河・遠江・三河を支配する今川義元が尾張へ侵攻し、織田信長がこれを迎え撃った。兵力は今川方が大きく上回っていたと伝えられるが、信長の軍が義元の本陣を突き、義元は討たれた。これにより今川氏の勢力は急速に衰え、人質同然の立場で今川方にあった松平元康（のちの徳川家康）が自立して三河へ戻る契機となった。信長にとっては領国の危機を脱し、以後の美濃・畿内への進出につながる転機となった戦いである。',
      3: '永禄3年5月19日（西暦1560年6月12日）、尾張国桶狭間で織田信長の軍と今川義元の軍が交戦し、義元が討死した。兵力は今川方2万5千、織田方2千から3千と伝えられるが、これらは『信長公記』などの記述に基づく数値で、実数として確かめられているわけではない。戦いの経過については長く「信長が迂回して背後から奇襲した」とする迂回奇襲説が通説であった。これは江戸時代の『甫庵信長記』や、明治期の陸軍参謀本部『日本戦史』に由来する理解である。しかし藤本正行らの研究により、一次史料に近い『信長公記』を読む限り、信長は桶狭間山に布陣する義元へ正面から攻めかかったと解されることが指摘され、現在は正面攻撃説が有力とされる。今川義元の出兵目的も、かつて言われた「上洛」ではなく尾張への進出であったとする見方が強い。合戦の場所についても、豊明市の伝承地とする説と名古屋市緑区の桶狭間とする説が分かれている。なお、合戦の周辺では村々が兵糧の徴発や略奪の被害を受けることが珍しくなく、勝敗の記録の背後にはそうした人びとの生活があったことも押さえておきたい。',
    },
    year: 1560,
    keywords: ['桶狭間', '織田信長', '今川義元', '尾張', '永禄3年', '奇襲説'],
    caveat:
      '桶狭間の戦いは長く「信長が迂回して奇襲した」と語られてきましたが、この迂回奇襲説は江戸時代の軍記物や明治期の戦史書に由来するもので、一次史料に近い『信長公記』からは正面から攻めかかったと読めます。近年は正面攻撃説が有力です。「2万5千対2千」という兵力も後世の記録によるもので確かな実数ではなく、今川義元の目的が上洛だったという説明も現在は疑問視されています。合戦地の位置についても複数の説があります。',
    gameNote:
      '盤上では桶狭間マスを第三章の固定イベントとし、投入兵が少なくても勝ちうる判定を置いています。これは「少数が大軍を破った」という結果を遊びの仕組みに翻訳したものです。実際の合戦がサイコロや札のような偶然だけで決まったという意味ではありません。',
  },
};

/** 9番。東海道が箱根の山地を越える地点。関東への関門。 */
export const HAKONE_SEKI: SquareBase = {
  id: 9,
  kind: 'barrier',
  name: '箱根関',
  reading: 'はこねのせき',
  region: 'kanto',
  note: {
    summary:
      '東海道が箱根の山地を越える難所。中世の関所は通行税である関銭を取るための施設で、街道に数多く置かれ流通の妨げにもなった。',
    detail: {
      1: '箱根の山をこえる道においてあった関所です。関所というと人をとりしまるところだと思われがちですが、この時代の関所はおもに通行料をとるためのものでした。あちこちにできすぎて、商人や旅人がこまることもありました。',
      2: '東海道が箱根の山地を越える地点。古代には箱根路が開かれ、足柄峠を越える足柄路とともに関東への出入り口として関が置かれた。中世の関所は治安の維持よりも、通行税である関銭や、湊で取る津料を徴収することが主な目的だった。荘園領主や寺社、幕府が収入を補うために街道や港・河川に関所を乱立させ、商品の流通を妨げている。京都の七口に関所を設けて関銭を集めた日野富子の例も知られる。織田信長は1568年以降、支配下の関所を撤廃して流通を促した。豊臣秀吉も同様の政策をとっている。1590年の小田原征伐では、豊臣軍が箱根の西を守る山中城を攻め落として関東へ入った。',
      3: '相模国足柄下郡の箱根は、東海道が険しい山地を越える難所であり、古代から東国への関門とされてきた。律令期には箱根峠を通る箱根路が開かれ、足柄峠を越える足柄路とともに関東の防衛と通行管理を担う関が置かれている。ただし中世の関所は近世のような取り締まりの施設ではなく、通行税（関銭）や湊での津料を徴収する経済的な施設としての性格が強い。室町期には荘園領主・寺社・幕府がそれぞれ関を設け、淀川筋や琵琶湖畔などでは短い区間にいくつもの関が並んで、商品の流通と価格を圧迫した。関銭を負担したのは商人だけではなく、伊勢参りなどの旅に出る一般の人びとや、荷を担いで売り歩く女性たちも同様である。織田信長は永禄11年(1568)に伊勢を支配下に置いた際、関所の撤廃と関銭徴収の停止を命じている。関東では小田原北条氏が箱根周辺を押さえて往来を管理したが、天正18年(1590)の小田原征伐で豊臣軍が伊豆の山中城を落として箱根を越え、北条氏は降伏した。なお芦ノ湖畔の有名な箱根関所は、江戸幕府が元和5年(1619)に設けたもので、戦国期のものではない。',
    },
    year: [1467, 1590],
    keywords: ['箱根', '関所', '関銭', '東海道', '小田原征伐', '流通'],
    caveat:
      '「入鉄砲に出女」で知られる芦ノ湖畔の箱根関所は、江戸幕府が元和5年(1619)に設けたもので、戦国時代には存在しません。中世の箱根の関は、人の取り締まりよりも通行税を取ることを主な目的とした施設でした。',
    gameNote:
      '盤上では関所に止まると足止めされ、銭を払えば通過できる扱いにしています。これは「関銭を取る」という中世の関所の性格を遊びに置き換えたもので、箱根に足止めの制度があったわけではありません。',
  },
};

/** 12番。関東側の評定マス。イベント札を引く。 */
export const HYOJO_KANTO: SquareBase = {
  id: 12,
  kind: 'council',
  name: '評定',
  reading: 'ひょうじょう',
  region: 'kanto',
  note: {
    summary:
      '大名とその家臣が集まって方針を決める合議の場。出陣や和睦の判断も、分国法のような法の制定も、この話し合いを経て行われた。',
    detail: {
      1: '評定（ひょうじょう）とは、大名とその家来たちが集まって話しあい、これからどうするかを決める会議のことです。戦いにいくかどうか、どんなきまりをつくるかも、ここで話しあわれました。',
      2: '評定は武家の合議のこと。鎌倉幕府では執権となった北条泰時が1225年に評定衆を置き、御成敗式目(1232)の制定など重要な事項を合議で決めた。室町幕府もこの仕組みを受け継いでいる。戦国大名の家中でも、当主が一族や重臣を集めて出陣・外交・領国支配の方針を決める寄合が開かれた。今川氏の「今川仮名目録」(1526)、伊達氏の「塵芥集」(1536)、武田氏の「甲州法度之次第」(1547)といった分国法も、当主の独断ではなく家中の合意を背景に定められている。当主が幼かったり不在だったりする場合には、一族の女性が政務を担うこともあった。',
      3: '評定は武家の合議による意思決定を指す語である。鎌倉幕府では北条泰時が嘉禄元年(1225)に評定衆を設置し、以後の訴訟と政務は執権・連署と評定衆の合議で処理された。貞永元年(1232)の御成敗式目もこの体制のもとで制定されている。室町幕府も評定衆・引付衆を置いたが、次第に将軍側近や管領による決定が優越していった。戦国大名の家中では、当主が一族・宿老・有力国人を集めて出陣、外交、検地、法度の制定などを議した。家臣には自立性の高い国人層が含まれるため、当主が一方的に命令できる関係ではなく、合意を形づくること自体が支配の条件だった。分国法の多くに家臣の署名や連判が伴うのはそのためである。また、当主が幼少あるいは不在のときに一族の女性が政務を代行した例もあり、今川氏親の妻である寿桂尼は自らの印判を用いて文書を発給し、三代にわたって今川家の政務に関与したことが知られる。なお「小田原評定」という言葉は、天正18年(1590)に小田原城で北条氏が籠城か決戦かを決めかねたという話に由来し、結論の出ない会議を指す慣用句として後世に広まったものである。',
    },
    year: [1225, 1590],
    keywords: ['評定', '合議', '評定衆', '分国法', '寿桂尼', '小田原評定'],
    caveat:
      '「小田原評定」という言い方は、結論の出ない長い会議のたとえとして後世に広まったものです。天正18年(1590)の小田原城で実際にどのような議論が行われたかを直接伝える同時代の史料は乏しく、「北条方はただ迷っていた」という評価は慎重に扱う必要があります。',
    gameNote:
      '盤上では評定マスに止まるとイベント札を引く扱いにしていますが、これは「その年に何が起きたか」をゲームに組み込むための仕組みです。実際の評定は偶然の出来事を引き当てる場ではなく、大名家の意思決定の手続きでした。',
  },
};

/** 15番。奥州への入口。古代の関で、戦国期には歌枕として知られていた。 */
export const SHIRAKAWA_SEKI: SquareBase = {
  id: 15,
  kind: 'barrier',
  name: '白河関',
  reading: 'しらかわのせき',
  region: 'tohoku',
  note: {
    summary:
      '古代に置かれた奥州への関門。平安時代なかばには関としての役目を終え、以後は和歌に詠まれる歌枕として記憶された。',
    detail: {
      1: 'いまの福島県白河市にあった、むかしの関所です。ずっと昔に奥州（東北）への入口としてつくられましたが、戦国時代にはもう関所としてははたらいていませんでした。かわりに、和歌によまれる有名な場所として知られていました。',
      2: '現在の福島県白河市旗宿のあたりにあったとされる古代の関。念珠ヶ関・勿来関とともに奥州三関と呼ばれ、東山道が陸奥へ入る地点を押さえて、通行の管理と軍事上の備えを担った。奈良・平安期には機能していたが、平安中期には関としての役目を終えている。以後「白河の関」は歌枕として和歌に詠み継がれ、能因法師の「都をば霞とともに立ちしかど秋風ぞ吹く白河の関」がよく知られる。戦国期のこの地方は白河結城氏が治め、常陸の佐竹氏と南奥の伊達氏の勢力が争う舞台となった。1590年の奥州仕置で白河結城氏は所領を失っている。1689年には松尾芭蕉が『おくのほそ道』の旅でこの地を訪れた。',
      3: '陸奥国白河郡、現在の福島県白河市旗宿付近に比定される古代の関。念珠ヶ関・勿来関とともに奥州三関と称され、東山道が陸奥へ入る地点を扼した。設置の時期は明確でないが、奈良・平安期には通行の管理と軍事上の備えとして機能していたとみられる。平安中期には関としての実質を失い、以後は「みちのくの入口」を象徴する歌枕として和歌に詠み継がれた。能因法師の「都をば霞とともに立ちしかど秋風ぞ吹く白河の関」や西行の歌が知られる。関の所在地は長く不明であったが、白河藩主松平定信が調査を行い、寛政12年(1800)に現在地を関跡と断定して「古関蹟」の碑を建てた。昭和34年(1959)から5年にわたる発掘調査では、空堀・土塁・柵列・門跡・竪穴住居跡・鍛冶工房跡などが検出され、縄文時代から古代・中世におよぶ複合遺跡であることが確かめられて、昭和41年(1966)に国史跡に指定されている。戦国期の白河地方は白河結城氏の支配下にあり、佐竹氏と伊達氏の勢力が交錯した。天正18年(1590)の奥州仕置により白河結城氏は改易されている。',
    },
    year: [1467, 1590],
    keywords: ['白河関', '奥州三関', '歌枕', '能因法師', '白河結城氏', '松平定信'],
    caveat:
      '白河関の正確な位置と設置年代は史料からは確定できません。現在「白河関跡」とされる旗宿の場所は、寛政12年(1800)に松平定信が考証して定めたもので、その後の発掘調査によって有力とされていますが、古代の関そのものが確認されたわけではありません。また戦国時代には関としては機能しておらず、歌枕として名が知られていた場所です。',
    gameNote:
      '盤上では白河関を関所マスとして奥州への入口に置いていますが、戦国時代の白河関は通行を取り締まる施設としては働いていません。奥州が遠く、たどり着きにくいという地理の感覚を盤上で表すための配置です。',
  },
};

/** 本ファイルが担当する特殊マス。盤面組み立て用。id 昇順。 */
export const SPECIAL_EAST_SQUARES: readonly SquareBase[] = [
  KYOTO,
  KUSATSU_SHUKU,
  OKEHAZAMA,
  HAKONE_SEKI,
  HYOJO_KANTO,
  SHIRAKAWA_SEKI,
];
